import type { GuideArticleRequest, GuideTopic } from '../types';

const SLUG_SEGMENT = /^[a-z0-9][a-z0-9-]{0,79}$/;

function parseQualifiedSlug(value: string): GuideArticleRequest | undefined {
  const segments = value.split('/');
  return segments.length === 2 && segments.every(segment => SLUG_SEGMENT.test(segment))
    ? { topic: segments[0], id: segments[1] }
    : undefined;
}

/**
 * Turn a route slug into an article request. A `topic/id` slug loads directly;
 * a bare article ID (a bookmark from before topic-qualified URLs, or an
 * authored `#/article_id` link) needs the published index. An article's own
 * slug is canonical because a hub may list it under another topic; index
 * snapshots without qualified slugs fall back to the listing topic.
 */
export function resolveGuideArticleRequest(
  slug: string | undefined,
  topics: GuideTopic[] = [],
): GuideArticleRequest | undefined {
  if (!slug) return undefined;
  const qualified = parseQualifiedSlug(slug);
  if (qualified) return qualified;
  if (!SLUG_SEGMENT.test(slug)) return undefined;

  for (const topic of topics) {
    const article = topic.articles.find(item => item.id === slug || item.slug === slug);
    if (!article) continue;
    const canonical = parseQualifiedSlug(article.slug);
    if (canonical) return canonical;
    if (SLUG_SEGMENT.test(topic.slug) && SLUG_SEGMENT.test(article.id)) {
      return { topic: topic.slug, id: article.id };
    }
  }
  return undefined;
}
